import React, { useEffect, useMemo, useState } from "react";
import { linksApi, graphApi, suggestionsApi } from "./api";
import { formatErr } from "../api";
import { toast } from "sonner";
import { Link2, Plus, Trash2, ShieldCheck, ShieldAlert, X, Search, ExternalLink, Sparkles, Check } from "lucide-react";
import { Link } from "react-router-dom";

// Map backend collection names to admin content route slugs.
const COLL_TO_TYPE_ROUTE = {
  articles: "articles", conditions: "conditions", faqs: "faqs", guidelines: "guidelines",
  research_papers: "research-papers", research: "research-updates",
  evidence_summaries: "evidence-summaries",
  treatments: "treatments", symptoms: "symptoms", investigations: "investigations",
  medicines: "medicines", trimesters: "trimesters",
  videos: "videos", patient_guides: "patient-guides", infographics: "infographics",
  books: "books", testimonials: "testimonials", media_appearances: "media-appearances",
};

const relBadge = (isDirectional, verified) => {
  if (!isDirectional) return "bg-[#3F6250]/10 text-[#3F6250]";
  return verified ? "bg-navy/10 text-navy" : "bg-[#D97706]/15 text-[#D97706]";
};

export default function Connections({ entityType, entityId, currentUserRole }) {
  const [taxonomy, setTaxonomy] = useState(null);
  const [links, setLinks] = useState({ outgoing: [], incoming: [] });
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const canVerify = ["super_administrator", "medical_reviewer"].includes(currentUserRole);
  const canCreateDirectional = ["super_administrator", "medical_reviewer", "medical_editor"].includes(currentUserRole);
  const canAct = ["super_administrator", "medical_reviewer", "medical_editor"].includes(currentUserRole);

  const reload = () => {
    if (!entityId) return;
    setLoading(true);
    Promise.all([
      graphApi.taxonomy(),
      linksApi.forEntity(entityType, entityId),
      suggestionsApi.forEntity(entityType, entityId).catch(() => []),
    ])
      .then(([tx, ls, sg]) => { setTaxonomy(tx); setLinks(ls); setSuggestions(sg || []); })
      .catch(e => toast.error(formatErr(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [entityType, entityId]);

  const verify = async (id) => {
    try { await linksApi.verify(id, true); toast.success("Link verified"); reload(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const unverify = async (id) => {
    try { await linksApi.verify(id, false); toast.success("Verification revoked"); reload(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this connection?")) return;
    try { await linksApi.remove(id); toast.success("Link removed"); reload(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const total = links.outgoing.length + links.incoming.length;
  const unverifiedCount = [...links.outgoing, ...links.incoming].filter(l => l.is_directional && !l.verified).length;

  return (
    <div className="bg-white border border-line" data-testid="connections-panel">
      <div className="px-6 py-4 border-b border-line flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link2 size={14} strokeWidth={1.5} className="text-prestige"/>
          <div className="mono text-[10px] uppercase tracking-widest text-prestige">Connections · Knowledge Graph</div>
          <span className="mono text-[10px] text-stone2 ml-2">{total} link{total !== 1 ? "s" : ""}</span>
          {unverifiedCount > 0 && (
            <span data-testid="connections-unverified-badge" className="mono text-[10px] uppercase tracking-widest bg-[#D97706]/15 text-[#D97706] px-2 py-0.5 ml-2">{unverifiedCount} awaiting verification</span>
          )}
        </div>
        <button data-testid="btn-add-connection" onClick={() => setShowAdd(true)} className="mono text-[10px] uppercase tracking-widest text-navy hover:text-terracotta inline-flex items-center gap-1">
          <Plus size={12}/> Add connection
        </button>
      </div>

      {loading ? (
        <div className="px-6 py-6 text-xs text-stone2">Loading…</div>
      ) : (
        <div className="divide-y divide-line">
          {suggestions.length > 0 && (
            <SuggestedLinks rows={suggestions} canAct={canAct}
                            onAccept={async (id) => { try { await suggestionsApi.accept(id); toast.success("Suggestion accepted"); reload(); } catch (e) { toast.error(formatErr(e.response?.data?.detail)); } }}
                            onReject={async (id) => { try { await suggestionsApi.reject(id); toast.success("Suggestion rejected"); reload(); } catch (e) { toast.error(formatErr(e.response?.data?.detail)); } }}/>
          )}
          <LinkList title="Outgoing" empty="No outgoing links yet." rows={links.outgoing} side="outgoing"
                    canVerify={canVerify} onVerify={verify} onUnverify={unverify} onRemove={remove}/>
          <LinkList title="Back-links" empty="No entities link to this one yet." rows={links.incoming} side="incoming"
                    canVerify={canVerify} onVerify={verify} onUnverify={unverify} onRemove={remove}/>
        </div>
      )}

      {showAdd && taxonomy && (
        <AddLinkModal
          entityType={entityType}
          entityId={entityId}
          taxonomy={taxonomy}
          canCreateDirectional={canCreateDirectional}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); reload(); }}
        />
      )}
    </div>
  );
}

function LinkList({ title, rows, side, empty, canVerify, onVerify, onUnverify, onRemove }) {
  return (
    <div className="px-6 py-4">
      <div className="mono text-[10px] uppercase tracking-widest text-stone2 mb-3">{title}</div>
      {rows.length === 0 ? (
        <div className="text-xs text-stone2">{empty}</div>
      ) : (
        <ul className="space-y-2">
          {rows.map(l => {
            const other = side === "outgoing" ? l.target : l.source;
            const arrow = side === "outgoing" ? "→" : "←";
            const route = other && COLL_TO_TYPE_ROUTE[other.type];
            return (
              <li key={l.id} data-testid={`link-${l.id}`} className="flex items-center justify-between gap-3 border border-line px-3 py-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`mono text-[10px] uppercase tracking-widest px-2 py-0.5 ${relBadge(l.is_directional, l.verified)}`}>
                      {l.relationship_type.replace(/_/g, " ")}
                    </span>
                    <span className="text-xs text-stone2">{arrow}</span>
                    <span className="mono text-[10px] uppercase tracking-widest text-stone2">{other?.type || "?"}</span>
                    {other && route ? (
                      <Link to={`/admin/content/${route}/${other.id}`} className="text-sm text-navy hover:text-terracotta truncate inline-flex items-center gap-1">
                        {other.title || other.slug}
                        <ExternalLink size={10}/>
                      </Link>
                    ) : (
                      <span className="text-sm text-navy truncate">{other?.title || other?.slug || "(missing)"}</span>
                    )}
                    {l.is_directional && !l.verified && (
                      <span className="mono text-[9px] uppercase tracking-widest text-[#D97706] inline-flex items-center gap-1">
                        <ShieldAlert size={10}/> unverified
                      </span>
                    )}
                    {l.is_directional && l.verified && (
                      <span className="mono text-[9px] uppercase tracking-widest text-[#3F6250] inline-flex items-center gap-1">
                        <ShieldCheck size={10}/> verified
                      </span>
                    )}
                    {l.source_of_link === "auto_suggested" && (
                      <span className="mono text-[9px] uppercase tracking-widest text-stone2">auto-suggested</span>
                    )}
                  </div>
                  {l.notes && <div className="text-xs text-stone2 mt-1 italic">"{l.notes}"</div>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {l.is_directional && !l.verified && canVerify && (
                    <button data-testid={`verify-${l.id}`} onClick={() => onVerify(l.id)} className="mono text-[10px] uppercase tracking-widest text-navy hover:text-terracotta inline-flex items-center gap-1">
                      <ShieldCheck size={11}/> Verify
                    </button>
                  )}
                  {l.is_directional && l.verified && canVerify && (
                    <button data-testid={`unverify-${l.id}`} onClick={() => onUnverify(l.id)} className="mono text-[10px] uppercase tracking-widest text-stone2 hover:text-[#9F1239] inline-flex items-center gap-1">
                      Unverify
                    </button>
                  )}
                  <button data-testid={`remove-${l.id}`} onClick={() => onRemove(l.id)} className="mono text-[10px] uppercase tracking-widest text-[#9F1239] hover:underline inline-flex items-center gap-1">
                    <Trash2 size={11}/> Remove
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function AddLinkModal({ entityType, entityId, taxonomy, canCreateDirectional, onClose, onSaved }) {
  const [rel, setRel] = useState("related_to");
  const [targetType, setTargetType] = useState("conditions");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const isDirectional = useMemo(
    () => (taxonomy?.relationship_types?.directional_clinical || []).some(r => r.key === rel),
    [rel, taxonomy]
  );

  useEffect(() => {
    let t = setTimeout(() => {
      graphApi.searchEntities(targetType, q).then(setResults).catch(() => setResults([]));
    }, 200);
    return () => clearTimeout(t);
  }, [targetType, q]);

  const save = async () => {
    if (!picked) { toast.error("Pick a target entity"); return; }
    if (isDirectional && !canCreateDirectional) { toast.error("Only Medical Editor/Reviewer can create directional clinical links"); return; }
    setSaving(true);
    try {
      await linksApi.create({
        source_type: entityType, source_id: entityId,
        target_type: picked.type, target_id: picked.id,
        relationship_type: rel, notes,
      });
      toast.success(isDirectional ? "Link created · awaiting verification" : "Link created");
      onSaved();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-navy/50 z-50 flex items-start justify-center pt-20 p-4" data-testid="add-link-modal">
      <div className="bg-white w-full max-w-2xl border border-line">
        <div className="px-6 py-4 border-b border-line flex items-center justify-between">
          <div className="font-serif text-xl text-navy">Add connection</div>
          <button onClick={onClose} data-testid="add-link-close" className="text-stone2 hover:text-navy"><X size={16}/></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-stone2 mb-1">Relationship</div>
            <select data-testid="rel-select" value={rel} onChange={e => setRel(e.target.value)} className="w-full border border-line px-3 py-2 text-sm">
              <optgroup label="Neutral (safe · auto-verified)">
                {(taxonomy.relationship_types.neutral || []).map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
              </optgroup>
              <optgroup label="Directional clinical (requires verification)">
                {(taxonomy.relationship_types.directional_clinical || []).map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
              </optgroup>
            </select>
            {isDirectional && (
              <div className="mono text-[10px] uppercase tracking-widest text-[#D97706] mt-2 inline-flex items-center gap-1">
                <ShieldAlert size={11}/> This link will be created unverified. A Medical Reviewer must verify before it appears on public pages.
              </div>
            )}
          </div>

          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-stone2 mb-1">Target type</div>
            <select data-testid="target-type-select" value={targetType} onChange={e => { setTargetType(e.target.value); setPicked(null); }} className="w-full border border-line px-3 py-2 text-sm">
              {(taxonomy.entity_types || []).map(t => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </select>
          </div>

          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-stone2 mb-1">Target entity</div>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone2"/>
              <input data-testid="target-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name or slug…" className="w-full pl-9 pr-3 py-2 border border-line text-sm"/>
            </div>
            <div className="mt-2 max-h-56 overflow-y-auto border border-line divide-y divide-line">
              {results.map(r => (
                <button key={r.id} data-testid={`opt-${r.id}`}
                  onClick={() => setPicked(r)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-clinical/60 ${picked?.id === r.id ? "bg-clinical/80" : ""}`}>
                  <div className="text-navy">{r.title || r.slug}</div>
                  <div className="mono text-[10px] text-stone2">{r.slug} · {r.status || "—"}</div>
                </button>
              ))}
              {results.length === 0 && <div className="px-3 py-3 text-xs text-stone2">No matches.</div>}
            </div>
          </div>

          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-stone2 mb-1">Notes (optional)</div>
            <textarea data-testid="link-notes" rows={2} value={notes} onChange={e => setNotes(e.target.value)} className="w-full border border-line px-3 py-2 text-sm"/>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-line flex items-center justify-end gap-2">
          <button onClick={onClose} className="mono text-[10px] uppercase tracking-widest text-stone2 hover:text-navy">Cancel</button>
          <button data-testid="btn-save-link" disabled={saving || !picked} onClick={save} className="bg-navy hover:bg-navy-hover text-white text-sm px-4 py-2 disabled:opacity-60 inline-flex items-center gap-2">
            <Plus size={12}/> {saving ? "Saving…" : "Create link"}
          </button>
        </div>
      </div>
    </div>
  );
}


function SuggestedLinks({ rows, canAct, onAccept, onReject }) {
  return (
    <div className="px-6 py-4 bg-[#F8F5EF]" data-testid="suggested-links">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={14} className="text-prestige"/>
        <div className="mono text-[10px] uppercase tracking-widest text-prestige">Suggested links</div>
        <span className="mono text-[10px] text-stone2 ml-1">{rows.length} pending · neutral only</span>
      </div>
      <ul className="space-y-2">
        {rows.map(s => (
          <li key={s.id} data-testid={`suggestion-${s.id}`} className="flex items-center justify-between gap-3 border border-line bg-white px-3 py-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="mono text-[10px] uppercase tracking-widest bg-[#3F6250]/10 text-[#3F6250] px-2 py-0.5">{s.relationship_type}</span>
                <span className="text-xs text-stone2">→</span>
                <span className="mono text-[10px] uppercase tracking-widest text-stone2">{s.target_type}</span>
                <span className="text-sm text-navy truncate">{s.target?.title || s.target?.slug || "(missing)"}</span>
                <span className="mono text-[9px] uppercase tracking-widest text-stone2">confidence {(s.confidence * 100).toFixed(0)}%</span>
              </div>
              {s.context_snippet && <div className="text-xs text-stone2 mt-1 italic truncate">"{s.context_snippet}"</div>}
            </div>
            {canAct && (
              <div className="flex items-center gap-2 shrink-0">
                <button data-testid={`sug-accept-${s.id}`} onClick={() => onAccept(s.id)} className="mono text-[10px] uppercase tracking-widest text-[#3F6250] hover:underline inline-flex items-center gap-1"><Check size={11}/> Accept</button>
                <button data-testid={`sug-reject-${s.id}`} onClick={() => onReject(s.id)} className="mono text-[10px] uppercase tracking-widest text-[#9F1239] hover:underline inline-flex items-center gap-1"><X size={11}/> Reject</button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
